<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { watch } from 'vue';
import { z } from 'zod';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import { useConfiguration, useUpdateConfiguration } from '~/features/cession/queries/useConfiguration';
import { premiumSchema } from '~/features/cession/schemas/premiumValidation';

const configurationSchema = z.object({
  enabled: z.boolean(),
  premium: premiumSchema,
});

type ConfigurationFormData = z.infer<typeof configurationSchema>;

const {
  data: configuration,
  isFetching,
  isLoading,
} = useConfiguration();

const updateMutation = useUpdateConfiguration();

const {
  defineField,
  errors,
  handleSubmit,
  setValues,
} = useForm<ConfigurationFormData>({
  initialValues: {
    enabled: false,
    premium: 0,
  },
  validationSchema: toTypedSchema(configurationSchema),
});

const [enabled] = defineField('enabled');
const [premium] = defineField('premium');

watch(() => configuration.value, (data) => {
  if (data?.automatic_cession) {
    setValues({
      enabled: data.automatic_cession.enabled,
      premium: data.automatic_cession.premium,
    });
  }
}, { immediate: true });

const handleSave = handleSubmit(async (values) => {
  await updateMutation.mutateAsync({
    automatic_cession: {
      enabled: values.enabled,
      premium: values.premium,
    },
  });
});
</script>

<template>
  <DashboardLayout title="Configurações" description="Configurações do sistema" :loading="isLoading || isFetching">
    <form @submit.prevent="handleSave">
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Configurações de Cessão Automática</h3>
        </div>

        <div class="card-body form-fields">
          <div class="field-group">
            <label class="form-label" for="enabled">
              Habilitar cessão automática
            </label>

            <div class="form-check form-switch">
              <input
                id="enabled"
                v-model="enabled"
                type="checkbox"
                class="form-check-input"
              />

              <span class="form-check-label">{{ enabled ? 'Habilitada' : 'Desabilitada' }}</span>
            </div>
          </div>

          <div class="field-group">
            <label class="form-label" for="premium">Valor do Ágio (%)</label>
            <input
              id="premium"
              v-model.number="premium"
              type="number"
              step="0.01"
              min="-5"
              max="5"
              class="form-control"
              :class="{ 'is-invalid': errors.premium }"
              placeholder="Digite o valor do ágio"
            />
            <div v-if="errors.premium" class="invalid-feedback">
              {{ errors.premium }}
            </div>
          </div>
        </div>

        <div class="card-body form-actions">
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="updateMutation.isPending.value"
          >
            <span v-if="updateMutation.isPending.value" class="spinner-border spinner-border-sm" />
            Salvar Alterações
          </button>
        </div>
      </div>
    </form>
  </DashboardLayout>
</template>

<style scoped lang="scss">
.form-fields {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
